import React, { useState, useEffect } from 'react'
import { Button, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IconContainer } from 'components/atoms/Icons'
import { ReactComponent as NoteTakerIcon } from 'assets/images/noteTaker.svg'
import { ReactComponent as AddNoteTakerIcon } from 'assets/images/addNoteTaker.svg'
import CommonDialog from '../notificationOverlay/CommonDialog'
import { patchRequest } from 'utils/api'
import { USER_CONNECTION_EVENTS_URL } from 'services/constants'
import moment from 'moment'
import NotificationSnackBar from '../notificationOverlay/NotificationSnackBar'
import OverlayLoading from 'components/atoms/OverlayLoading'
import LinkCalendarDialog from '../oAuth/LinkCalendarDialog'
import colors from 'styles/colors'
import { trackMeetingBotSet } from 'services/amplitudeMeetingTracking'
import { updatePageState } from 'utils/windowHistoryHelper'
import RowStack from 'components/atoms/container/RowStack'
import noteTakerArrow from 'assets/images/noteTakerArrow.gif'
import ImageContainer from 'components/atoms/container/ImageContainer'
import { patchUserCalendarEvent } from 'services/userConnectionsHelper'

const MeetingNoteTakerButton = ({
  event,
  meetingUrl,
  setMeetingUrl,
  hasBot,
  setHasBot,
  calendarNotConnected,
  setCalendarNotConnected,
  buttonProps = {}
}) => {
  const { t } = useTranslation()
  const [existDialogOpen, setExistDialogOpen] = useState(false)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [noMeetingDialogOpen, setNoMeetingDialogOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [updateError, setUpdateError] = useState(false)
  const [updateSuccess, setUpdateSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false)
  const [reAddDialogOpen, setReAddDialogOpen] = useState(false)

  const updateMeetingBot = async (recordMeeting, videoConference) => {
    setLoading(true)
    let success = false
    const request = {
      recordMeeting
    }
    if (videoConference) {
      request.videoConference = videoConference
    }
    const res = await patchRequest(
      `${USER_CONNECTION_EVENTS_URL}/${event.userConnectionEventId}`,
      request
    )
    if (res.status === 200) {
      event.botId = recordMeeting
      if (recordMeeting) {
        setSuccessMessage(t('myTeam.noteTakerAdded'))
        if (videoConference) {
          setMeetingUrl(res.data.data.userCalendarEvents?.[0].meetingUrl)
        }
        trackMeetingBotSet(event, '1:1 meeting page')
      } else {
        setSuccessMessage(t('myTeam.noteTakerRemoved'))
        trackMeetingBotSet(event, undefined, false)
      }
      setUpdateSuccess(true)
      setHasBot(recordMeeting)
      success = true
    } else {
      setUpdateError(true)
    }
    setLoading(false)
    return success
  }
  const handleReAddBot = async reAdd => {
    if (reAdd) {
      setLoading(true)
      const res = await patchUserCalendarEvent(event.id, {
        reAddBot: true
      })
      if (event.botId !== res?.botId) {
        event.canReAddBot = false
      }
      setLoading(false)
    }

    setReAddDialogOpen(false)
  }
  const handleClick = (isCalendarNotConnected = calendarNotConnected) => {
    if (event.canReAddBot) {
      setReAddDialogOpen(true)
    } else if (isCalendarNotConnected) {
      setCalendarDialogOpen(true)
    } else if (hasBot) {
      setExistDialogOpen(true)
    } else if (meetingUrl) {
      setAddDialogOpen(true)
    } else {
      setNoMeetingDialogOpen(true)
    }
  }

  useEffect(() => {
    if (window.history?.state?.usr?.openAddNoteTakerSingleMeeting) {
      handleClick()
      updatePageState({
        usr: {
          userCalendarEventId: event?.id,
          openAddNoteTakerSingleMeeting: false
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.history?.state])

  const handleExistDialogClose = remove => {
    setExistDialogOpen(false)
    if (remove) {
      setRemoveDialogOpen(true)
    }
  }

  const handleRemoveDialogClose = async remove => {
    if (remove) {
      const updated = await updateMeetingBot(false)
      if (updated) {
        setRemoveDialogOpen(false)
      }
    } else {
      setRemoveDialogOpen(false)
    }
  }

  const handleAddDialogClose = async add => {
    if (add) {
      const updated = await updateMeetingBot(true)
      if (updated) {
        setAddDialogOpen(false)
      }
    } else {
      setAddDialogOpen(false)
    }
  }

  const handleAddConferenceClose = async add => {
    if (add) {
      const updated = await updateMeetingBot(true, true)
      if (updated) {
        setNoMeetingDialogOpen(false)
      }
    } else {
      setNoMeetingDialogOpen(false)
    }
  }

  const handleSigninResult = async success => {
    if (success) {
      setCalendarNotConnected(false)
      handleClick(false)
    }
    setCalendarDialogOpen(false)
  }

  const meetingStarted = () =>
    moment.utc(event.startTime).isBefore(moment.utc())
  return (
    <>
      <OverlayLoading open={loading} />
      <RowStack spacing={1} width={'auto'}>
        {event.canReAddBot && (
          <RowStack spacing={2} width={'auto'}>
            <Typography variant='body1KindaSmall'>
              {t('myTeam.reAddNoteTaker')}
            </Typography>

            <ImageContainer src={noteTakerArrow} maxWidth={28} maxHeight={16} />
          </RowStack>
        )}
        <Button
          startIcon={
            <IconContainer
              icon={hasBot ? <NoteTakerIcon /> : <AddNoteTakerIcon />}
            />
          }
          onClick={() => handleClick()}
          variant='outlined'
          color='primary'
          size='small'
          sx={{
            '&:hover': {
              '--path-color': colors.White
            }
          }}
          {...buttonProps}>
          {hasBot ? t('myTeam.noteTaker') : t('contact.addNoteTaker')}
        </Button>
      </RowStack>
      <LinkCalendarDialog
        handleSigninResult={handleSigninResult}
        dialogOpen={calendarDialogOpen}
        setDialogOpen={setCalendarDialogOpen}
      />
      <CommonDialog
        open={existDialogOpen}
        onClose={handleExistDialogClose}
        titleText={t('myTeam.noteTakerAlreadyAdded')}
        notificationText={t('myTeam.noteTakerAlreadyAddedDescription')}
        confirmText={t('rolePlay.okay')}
        cancelText={meetingStarted() ? undefined : t('common.remove')}
        confirmValue={0}
        cancelValue={1}
      />
      <CommonDialog
        open={removeDialogOpen}
        onClose={handleRemoveDialogClose}
        titleText={t('myTeam.removeNotetaker')}
        notificationText={t('myTeam.removeNotetakerDescription')}
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
      />
      <CommonDialog
        open={addDialogOpen}
        onClose={handleAddDialogClose}
        titleText={t('myTeam.takeNoteForYou')}
        notificationText={t('myTeam.takeNoteForYouDescription')}
        confirmText={t('contact.addNoteTaker')}
        cancelText={t('common.notRightNow')}
      />
      <CommonDialog
        open={noMeetingDialogOpen}
        onClose={handleAddConferenceClose}
        titleText={t('myTeam.addConferenceToUseNoteTaker')}
        notificationText={t('myTeam.addConferenceToUseNoteTakerDescription')}
        cancelText={t('common.notRightNow')}
        confirmText={t('myTeam.yesAddBoth')}
      />
      <CommonDialog
        open={reAddDialogOpen}
        onClose={handleReAddBot}
        titleText={t('myTeam.reInviteNoteTaker')}
        notificationText={t('myTeam.noteTakerNotJoin')}
        confirmText={t('contact.addNoteTaker')}
        cancelText={t('common.cancel')}
      />
      <NotificationSnackBar
        open={updateError}
        error={true}
        setOpen={setUpdateError}
      />
      <NotificationSnackBar
        open={updateSuccess}
        success={true}
        successText={successMessage}
        setOpen={setUpdateSuccess}
      />
    </>
  )
}

export default MeetingNoteTakerButton
